import React, { useState, useEffect } from 'react';
import { BLOG_POSTS, BlogPost, getBlogPostBySlug, BlogComment } from '../data/blogs';
import { useProducts } from '../context/ProductContext';
import { useQuote } from '../context/QuoteContext';
import { SafeImage } from '../components/SafeImage';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { formatKsh } from '../utils/formatters';
import { buildCleanPath } from '../utils/navigation';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Heart,
  Share2,
  Check,
  Copy,
  MessageSquare,
  Sparkles,
  ShoppingBag,
  ExternalLink,
  ShieldCheck,
  Send,
  User,
  ArrowRight,
  Bookmark
} from 'lucide-react';

interface BlogDetailPageProps {
  slug: string;
  onNavigate: (page: string, param?: string) => void;
}

export const BlogDetailPage: React.FC<BlogDetailPageProps> = ({ slug, onNavigate }) => {
  const [post, setPost] = useState<BlogPost | null>(() => getBlogPostBySlug(slug) || BLOG_POSTS[0]);
  const [likesCount, setLikesCount] = useState<number>(post?.likes || 0);
  const [hasLiked, setHasLiked] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // Comments state
  const [comments, setComments] = useState<BlogComment[]>(post?.comments || []);
  const [commentName, setCommentName] = useState('');
  const [commentLocation, setCommentLocation] = useState('');
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentSuccessMessage, setCommentSuccessMessage] = useState(false);

  const { products } = useProducts();
  const { addToQuote } = useQuote();

  // Load post from API or fallback with full field preservation
  useEffect(() => {
    let isMounted = true;
    const fetchPost = async () => {
      try {
        const localFallback = getBlogPostBySlug(slug) || BLOG_POSTS[0];
        const res = await fetch(`/api/blogs/${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.blog && isMounted) {
            const raw = data.blog;
            const merged: BlogPost = {
              ...localFallback,
              ...raw,
              author: { ...localFallback.author, ...(raw.author || {}) },
              tableOfContents: (raw.tableOfContents && raw.tableOfContents.length > 0) ? raw.tableOfContents : localFallback.tableOfContents,
              keyTakeaways: (raw.keyTakeaways && raw.keyTakeaways.length > 0) ? raw.keyTakeaways : localFallback.keyTakeaways,
              contentSections: (raw.contentSections && raw.contentSections.length > 0) ? raw.contentSections : localFallback.contentSections,
              relatedProductIds: (raw.relatedProductIds && raw.relatedProductIds.length > 0) ? raw.relatedProductIds : localFallback.relatedProductIds,
              seo: { ...localFallback.seo, ...(raw.seo || {}) },
              comments: Array.isArray(raw.comments) && raw.comments.length > 0 ? raw.comments : (localFallback.comments || []),
            };
            setPost(merged);
            setLikesCount(typeof raw.likes === 'number' ? raw.likes : merged.likes);
            setComments(merged.comments || []);
            return;
          }
        }
        if (localFallback && isMounted) {
          setPost(localFallback);
          setLikesCount(localFallback.likes);
          setComments(localFallback.comments || []);
        }
      } catch {
        const local = getBlogPostBySlug(slug) || BLOG_POSTS[0];
        if (local && isMounted) {
          setPost(local);
          setLikesCount(local.likes);
          setComments(local.comments || []);
        }
      }
    };

    fetchPost();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Dynamic SEO Configuration & JSON-LD
  useEffect(() => {
    if (!post) return;

    // 1. Update Title and Meta Description
    document.title = `${post.seo.metaTitle || post.title} | Saints Tech Solutions`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', post.seo.metaDescription || post.excerpt);
    }

    // 2. OpenGraph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', post.title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', post.excerpt);

    // 3. Structured Data JSON-LD Schema.org BlogPosting
    const scriptId = 'blog-post-ld-json';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      'headline': post.title,
      'description': post.excerpt,
      'image': `https://saintstech.co.ke${post.image}`,
      'datePublished': '2026-10-14T08:00:00+03:00',
      'dateModified': '2026-10-16T12:00:00+03:00',
      'author': {
        '@type': 'Person',
        'name': post.author.name,
        'jobTitle': post.author.role
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Saints Tech Solutions',
        'logo': 'https://saintstech.co.ke/logo.png'
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': post.seo.canonicalUrl || `https://saintstech.co.ke/blog/${post.slug}`
      }
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-bold text-slate-900">Article Not Found</h2>
        <p className="text-slate-600 text-sm">
          The requested technical insight could not be located.
        </p>
        <button
          onClick={() => onNavigate('blog')}
          className="px-4 py-2 bg-sky-600 text-white rounded-xl text-xs font-semibold"
        >
          Return to Blog Hub
        </button>
      </div>
    );
  }

  // Handle Likes
  const handleLike = async () => {
    if (hasLiked) return;
    setHasLiked(true);
    setLikesCount((prev) => prev + 1);

    try {
      await fetch(`/api/blogs/${post.slug}/react`, {
        method: 'POST'
      });
    } catch {
      // ignore
    }
  };

  // Handle Share Copy
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  // Handle Comment Submission
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentName.trim() || !commentText.trim()) return;

    setIsSubmittingComment(true);
    try {
      const res = await fetch(`/api/blogs/${post.slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: commentName,
          location: commentLocation || 'Kenya',
          comment: commentText
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.comment) {
          setComments((prev) => [data.comment, ...prev]);
        }
      } else {
        // Local fallback
        const fallbackComment: BlogComment = {
          id: 'c_' + Date.now(),
          name: commentName.trim(),
          location: commentLocation.trim() || 'Kenya',
          comment: commentText.trim(),
          date: 'Just now'
        };
        setComments((prev) => [fallbackComment, ...prev]);
      }

      setCommentName('');
      setCommentLocation('');
      setCommentText('');
      setCommentSuccessMessage(true);
      setTimeout(() => setCommentSuccessMessage(false), 4000);
    } catch {
      const fallbackComment: BlogComment = {
        id: 'c_' + Date.now(),
        name: commentName.trim(),
        location: commentLocation.trim() || 'Kenya',
        comment: commentText.trim(),
        date: 'Just now'
      };
      setComments((prev) => [fallbackComment, ...prev]);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-bold text-slate-900">Article Not Found</h2>
        <p className="text-slate-600 text-sm">
          The requested technical insight or article could not be located.
        </p>
        <button
          onClick={() => onNavigate('blog')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white rounded-xl text-xs font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Blog & Insights</span>
        </button>
      </div>
    );
  }

  // Related products objects
  const relatedProducts = products.filter((p) => (post?.relatedProductIds || []).includes(p.id));

  // Next/prev posts
  const otherPosts = BLOG_POSTS.filter((b) => b.id !== post.id);

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb={post.category}
        parentPage={{ label: 'Blog & Insights', page: 'blog' }}
        badgeIcon={<Sparkles className="w-3.5 h-3.5" />}
        badgeText={`${post.category} · ${post.publishedDate} · ${post.readTime}`}
        badgeColor="emerald"
        title={post.title}
        description={post.excerpt}
        backgroundImage={post.image}
        fallbackCategory="blog"
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onNavigate('blog')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>All Insights</span>
            </button>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg text-xs text-white">
              <span className="font-semibold">{post.author.name}</span>
              <span className="text-white/60">·</span>
              <span className="text-white/80">{post.author.role}</span>
            </div>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              {copySuccess ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copySuccess ? 'Link Copied!' : 'Copy Link'}</span>
            </button>
            <button
              onClick={handleLike}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium backdrop-blur-md border transition-colors cursor-pointer ${
                hasLiked
                  ? 'bg-rose-500/30 border-rose-400 text-white'
                  : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${hasLiked ? 'fill-rose-400 text-rose-400' : ''}`} />
              <span>{likesCount} Likes</span>
            </button>
          </div>
        }
      />


      {/* Main Image Banner */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 my-8">
        <div className="aspect-video sm:aspect-21/9 rounded-3xl overflow-hidden shadow-lg border border-slate-200 bg-slate-900">
          <SafeImage
            src={post.image}
            fallbackCategory="general"
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Main Article Body & Table of Contents */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left Column: Table of Contents & Quick Action Widget */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-24 space-y-6">
              {/* Table of Contents */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Article Contents
                </div>
                <nav className="space-y-1">
                  {(post.tableOfContents || []).map((item, idx) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-xs text-slate-600 hover:text-sky-700 hover:bg-sky-50 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <span className="text-slate-400 font-mono mr-1.5">{idx + 1}.</span>
                      <span>{item.title}</span>
                    </a>
                  ))}
                </nav>
              </div>

              {/* Consultation / Quotation Card */}
              <div className="bg-gradient-to-br from-slate-900 to-sky-950 text-white p-5 rounded-2xl border border-slate-800 space-y-3 shadow-sm">
                <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400">
                  Engineering Desk
                </span>
                <h4 className="text-sm font-bold text-white leading-snug">
                  Interested in implementing these systems for your business?
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  We fabricate certified food-grade machines calibrated for Kenyan conditions.
                </p>
                <div className="pt-1">
                  <button
                    onClick={() => onNavigate('contact')}
                    className="w-full py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>Request Custom Quote</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Right Column: Editorial Body */}
          <main className="lg:col-span-8 space-y-10">
            {/* Executive Key Takeaways Box */}
            <div className="bg-sky-50/70 rounded-2xl p-6 border border-sky-200/80 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-900">
                <Sparkles className="w-4 h-4 text-sky-600" />
                <span>Executive Summary & Key Takeaways</span>
              </div>
              <ul className="space-y-2">
                {(post.keyTakeaways || []).map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-700 leading-relaxed">
                    <Check className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sections */}
            <div className="space-y-10 prose prose-slate max-w-none">
              {(post.contentSections || []).map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28 space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight pb-2 border-b border-slate-200">
                    {section.heading}
                  </h2>

                  <div className="space-y-4 text-slate-700 text-sm sm:text-base leading-relaxed">
                    {(section.paragraphs || []).map((para, pIdx) => (
                      <p key={pIdx}>{para}</p>
                    ))}
                  </div>

                  {section.highlightQuote && (
                    <blockquote className="my-6 p-4 sm:p-5 bg-slate-100/80 rounded-2xl border-l-4 border-sky-600 text-slate-800 italic text-sm sm:text-base leading-relaxed">
                      "{section.highlightQuote}"
                    </blockquote>
                  )}

                  {section.bulletPoints && section.bulletPoints.length > 0 && (
                    <ul className="space-y-2 my-4 pl-2">
                      {(section.bulletPoints || []).map((b, bIdx) => (
                        <li key={bIdx} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-600 mt-2 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            {/* Featured Relevant Machinery Strip */}
            {relatedProducts.length > 0 && (
              <div className="pt-8 border-t border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
                      Engineering Solutions
                    </span>
                    <h3 className="text-lg font-bold text-slate-900">
                      Machinery Highlighted in This Analysis
                    </h3>
                  </div>
                  <button
                    onClick={() => onNavigate('products')}
                    className="text-xs font-semibold text-sky-700 hover:text-sky-800"
                  >
                    View All Products ↗
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(relatedProducts || []).map((prod) => (
                    <div
                      key={prod.id}
                      className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs flex flex-col justify-between space-y-3"
                    >
                      <div className="flex items-center gap-3">
                        <SafeImage
                          src={prod.image}
                          fallbackCategory={prod.category}
                          alt={prod.name}
                          className="w-14 h-14 rounded-xl object-cover bg-slate-100 shrink-0 border border-slate-200"
                        />
                        <div className="min-w-0">
                          <h4 className="font-bold text-xs text-slate-900 truncate">{prod.name}</h4>
                          <div className="text-[11px] text-slate-500">{prod.capacity}</div>
                          <div className="text-xs font-bold text-sky-700 tabular-nums mt-0.5">
                            KSh {formatKsh(prod.priceKsh)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <button
                          onClick={() => onNavigate('product-detail', prod.id)}
                          className="flex-1 py-1.5 text-center text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                        >
                          Specifications
                        </button>
                        <button
                          onClick={() => addToQuote(prod, 1)}
                          className="flex-1 py-1.5 text-center text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-1"
                        >
                          <ShoppingBag className="w-3.5 h-3.5" />
                          <span>Add to Quote</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Author Bio Box */}
            <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-center sm:items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-slate-900 text-white font-bold text-base flex items-center justify-center shrink-0">
                {post.author.avatarInitials}
              </div>
              <div className="space-y-1.5 text-center sm:text-left">
                <div className="text-sm font-bold text-slate-900">{post.author.name}</div>
                <div className="text-xs text-sky-700 font-medium">{post.author.role}</div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Specializing in dairy pasteurization thermodynamics, water purification membrane engineering, and automated micro-dispensing electronics across East Africa. Based at Saints Tech Solutions Nairobi workshop.
                </p>
              </div>
            </div>

            {/* Interactive Comment & Community Discussion */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-2xs space-y-6">
              <div className="border-b border-slate-200 pb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-sky-600" />
                    <span>Technical Discussion & Inquiries</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Have questions about this equipment or Kenyan installation? Ask our team below.
                  </p>
                </div>
                <span className="text-xs font-bold text-slate-700 tabular-nums">
                  {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
                </span>
              </div>

              {/* Comment submission form */}
              <form onSubmit={handleSubmitComment} className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-200/80">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Samuel Mutua"
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      className="w-full text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Location / County</label>
                    <input
                      type="text"
                      placeholder="e.g. Kiambu / Eldoret"
                      value={commentLocation}
                      onChange={(e) => setCommentLocation(e.target.value)}
                      className="w-full text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Comment or Question *</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Ask a technical or pricing question about this machinery..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="w-full text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div className="flex items-center justify-between pt-1">
                  {commentSuccessMessage ? (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>Thank you! Your comment was posted.</span>
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-400">
                      Moderated by Saints Tech Solutions technical staff.
                    </span>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmittingComment}
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 shadow-xs"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{isSubmittingComment ? 'Posting...' : 'Post Comment'}</span>
                  </button>
                </div>
              </form>

              {/* List of comments */}
              <div className="space-y-4 pt-2">
                {comments.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">
                    Be the first to leave a comment or ask an engineering question!
                  </p>
                ) : (
                  (comments || []).map((c) => (
                    <div key={c.id} className="p-4 bg-slate-50/80 rounded-xl border border-slate-200 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-slate-900">{c.name}</span>
                          <span className="text-[11px] text-slate-400">({c.location})</span>
                        </div>
                        <span className="text-[10px] text-slate-400">{c.date}</span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">{c.comment}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Read Next Section */}
            <div className="pt-8 border-t border-slate-200 space-y-4">
              <h3 className="text-base font-bold text-slate-900">More Field Analyses</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(otherPosts || []).slice(0, 2).map((other) => (
                  <div
                    key={other.id}
                    onClick={() => {
                      onNavigate('blog-detail', other.slug);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="p-4 bg-white rounded-2xl border border-slate-200 hover:border-slate-300 transition-all cursor-pointer group space-y-2"
                  >
                    <div className="text-[10px] uppercase font-bold text-sky-700">{other.category}</div>
                    <h4 className="font-bold text-xs text-slate-900 leading-snug group-hover:text-sky-700 transition-colors line-clamp-2">
                      {other.title}
                    </h4>
                    <div className="text-[11px] text-slate-400 flex items-center justify-between pt-1">
                      <span>{other.readTime}</span>
                      <span className="text-sky-700 font-semibold group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
